import { useState, useEffect } from 'react';
import { BookOpen, Upload, MessageSquare, Send, Menu, X, Edit2, Trash2, Users, UserPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Use local .mjs file from public folder
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// API URL - automatically switches between local and production
const API_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:5001'
  : 'https://d3lmve5rhj7a3d.cloudfront.net';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [currentPage, setCurrentPage] = useState('chapters');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    setIsAuthenticated(true);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentPage('chapters');
  };

  const handleDeleteChapter = async (chapterId) => {
    if (window.confirm('Are you sure you want to delete this chapter?')) {
      try {
        const response = await fetch(`${API_URL}/api/chapters/${chapterId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setChapters(chapters.filter(ch => ch.id !== chapterId));
          alert('Chapter deleted successfully');
        } else {
          alert('Failed to delete chapter');
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete chapter');
      }
    }
  };

  const pages = {
    chapters: <ChaptersPage 
      chapters={chapters} 
      setChapters = {setChapters}
      onSelectChapter={(ch) => { setSelectedChapter(ch); setCurrentPage('reader'); }}
      userRole={user?.role}
      onDeleteChapter={handleDeleteChapter}
      token = {token}
    />,
    reader: <ReaderPage 
      chapter={selectedChapter} 
      onBack={() => setCurrentPage('chapters')}
      user={user}
      token={token}
    />,
    upload: <UploadPage 
      onUpload={(chapter) => { setChapters([...chapters, chapter]); setCurrentPage('chapters'); }}
      token={token}
    />,
    users: <UserManagementPage token={token} />
  };

  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const canUpload = user?.role === 'admin' || user?.role === 'master';
  const isMaster = user?.role === 'master';
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-indigo-600" />
              <span className="ml-2 text-xl font-bold text-gray-800">Book Review Blog</span>
              {user && (
                <span className="ml-3 px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-full font-semibold">
                  {user.role === 'master' ? 'MASTER' : user.role.toUpperCase()}
                </span>
              )}
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <NavLink active={currentPage === 'chapters'} onClick={() => setCurrentPage('chapters')}>
                Chapters
              </NavLink>
              {canUpload && (
                <NavLink active={currentPage === 'upload'} onClick={() => setCurrentPage('upload')}>
                  Upload
                </NavLink>
              )}
              {isMaster && (
                <NavLink active={currentPage === 'users'} onClick={() => setCurrentPage('users')}>
                  Users
                </NavLink>
              )}
              <span className="text-sm text-gray-600">Hi, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                Logout
              </button>
            </div>

            <div className="md:hidden flex items-center">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-600 hover:text-gray-900">
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <MobileNavLink active={currentPage === 'chapters'} onClick={() => { setCurrentPage('chapters'); setMobileMenuOpen(false); }}>
                Chapters
              </MobileNavLink>
              {canUpload && (
                <MobileNavLink active={currentPage === 'upload'} onClick={() => { setCurrentPage('upload'); setMobileMenuOpen(false); }}>
                  Upload
                </MobileNavLink>
              )}
              {isMaster && (
                <MobileNavLink active={currentPage === 'users'} onClick={() => { setCurrentPage('users'); setMobileMenuOpen(false); }}>
                  Users
                </MobileNavLink>
              )}
              <div className="px-3 py-2 text-sm text-gray-600">Hi, {user?.name}</div>
              <button
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50"
              >
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {pages[currentPage]}
      </main>
    </div>
  );
}

// Navigation Components
function NavLink({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${active ? 'text-indigo-600 bg-indigo-50' : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50'}`}>
      {children}
    </button>
  );
}

function MobileNavLink({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium ${active ? 'text-indigo-600 bg-indigo-50' : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50'}`}>
      {children}
    </button>
  );
}

// Login Page
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!username || !password) {
      setError('Please enter username and password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        onLogin({ role: data.role, name: data.name, username: data.username }, data.token);
      } else {
        setError(data.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8 space-y-6">
        <div className="text-center">
          <BookOpen className="mx-auto h-16 w-16 text-indigo-600" />
          <h1 className="mt-4 text-3xl font-bold text-gray-900">Book Review Blog For Breaking Curses</h1>
          <p className="mt-2 text-sm text-gray-600">Sign in to continue</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="Enter username"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="Enter password"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>
      </div>
    </div>
  );
}

// User Management Page
function UserManagementPage({ token }) {
  const [users, setUsers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '', name: '', role: 'viewer' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.username || !formData.password || !formData.name) {
      alert('All fields required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('User created successfully');
        setFormData({ username: '', password: '', name: '', role: 'viewer' });
        setShowCreateForm(false);
        fetchUsers();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to create user');
      }
    } catch (error) {
      alert('Error creating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('User deleted');
        fetchUsers();
      }
    } catch (error) {
      alert('Error deleting user');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-900">User Management</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Create User
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Create New User</h2>
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Username"
              value={formData.username}
              onChange={(e) => setFormData({...formData, username: e.target.value})}
              className="px-3 py-2 border rounded-md"
            />
            <input
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="px-3 py-2 border rounded-md"
            />
            <input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="px-3 py-2 border rounded-md"
            />
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              className="px-3 py-2 border rounded-md"
            >
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={handleCreateUser} className="px-4 py-2 bg-indigo-600 text-white rounded-md">
              Create
            </button>
            <button onClick={() => setShowCreateForm(false)} className="px-4 py-2 bg-gray-300 rounded-md">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map(user => (
              <tr key={user._id}>
                <td className="px-6 py-4 whitespace-nowrap">{user.username}</td>
                <td className="px-6 py-4 whitespace-nowrap">{user.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleDeleteUser(user._id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Chapters Page
function ChaptersPage({ chapters, setChapters, onSelectChapter, userRole, onDeleteChapter, token }) {

  useEffect(() => {
    fetchChapters();
  }, []);

  const fetchChapters = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chapters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChapters(data);
      }
    } catch (error) {
      console.error('Error fetching chapters on load:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold text-gray-900">Book Chapters</h1>
        <span className="text-sm text-gray-600">{chapters.length} chapters available</span>
      </div>
      
      {chapters.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No chapters yet</h3>
          <p className="mt-1 text-sm text-gray-500">Upload your first chapter to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {chapters.map((chapter, idx) => (
            <ChapterCard 
              key={idx} 
              chapter={chapter} 
              onClick={() => onSelectChapter(chapter)}
              userRole={userRole}
              onDelete={onDeleteChapter}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChapterCard({ chapter, onClick, userRole, onDelete }) {
  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(chapter.id);
  };

  const canDelete = userRole === 'admin' || userRole === 'master';

  return (
    <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-indigo-500 relative">
      <div onClick={onClick}>
        <div className="flex items-start justify-between mb-3">
          <BookOpen className="h-8 w-8 text-indigo-600" />
          <span className="text-xs text-gray-500">{chapter.pages} pages</span>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{chapter.title}</h3>
        <p className="text-sm text-gray-600 mb-4">{chapter.description}</p>
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{chapter.date}</span>
          <div className="flex items-center">
            <MessageSquare className="h-4 w-4 mr-1" />
            <span>{chapter.commentCount || 0}</span>
          </div>
        </div>
      </div>
      {canDelete && (
        <button
          onClick={handleDelete}
          className="absolute top-2 right-2 p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
          title="Delete chapter"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

// Upload Page
function UploadPage({ onUpload, token }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFileName(file.name);
      setPdfFile(file);
      
      // Extract page count
      try {
        const arrayBuffer = await file.arrayBuffer();
        
        // Load PDF.js from CDN
        const pdfjsLib = window['pdfjs-dist/build/pdf'];
        
        if (!pdfjsLib) {
          // Fallback if library not loaded
          console.warn('PDF.js not loaded, page count unavailable');
          setPageCount(0);
          return;
        }
        
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        setPageCount(pdf.numPages);
        console.log('PDF loaded, pages:', pdf.numPages);
      } catch (error) {
        console.error('Error reading PDF:', error);
        // Don't fail the upload, just show 0 pages
        setPageCount(0);
      }
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleSubmit = async () => {
    if (!title || !description || !pdfFile) {
      alert('Please fill in all fields and select a PDF');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('pdf', pdfFile);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('pages', pageCount);

      const response = await fetch(`${API_URL}/api/chapters`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const newChapter = await response.json();
      onUpload({...newChapter, comments: []});

      setTitle('');
      setDescription('');
      setPdfFile(null);
      setFileName('');
      setPageCount(0);
      alert('Chapter uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload chapter. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-4xl font-bold text-gray-900">Upload Chapter</h1>
      
      <div className="bg-white p-8 rounded-lg shadow-md space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Chapter Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Chapter 1: The Beginning"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this chapter..."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">PDF File</label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-indigo-500 transition-colors">
            <div className="space-y-1 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                  <span>Upload a file</span>
                  <input type="file" accept=".pdf" onChange={handleFileChange} className="sr-only" />
                </label>
              </div>
              <p className="text-xs text-gray-500">PDF up to 10MB</p>
              {fileName && (
                <div className="text-sm">
                  <p className="text-indigo-600 font-medium">{fileName}</p>
                  {pageCount > 0 && <p className="text-gray-600">{pageCount} pages detected</p>}
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={isUploading}
          className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? 'Uploading to AWS S3...' : 'Upload Chapter'}
        </button>
      </div>
    </div>
  );
}

// Reader Page
function ReaderPage({ chapter, onBack, user, token }) {
  const [comments, setComments] = useState(chapter?.comments || []);
  const [newComment, setNewComment] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1)
;

  useEffect(() => {
    if (chapter) {
      fetch(`${API_URL}/api/chapters/${chapter.id}`)
        .then(res => res.json())
        .then(data => {
          setPdfUrl(data.pdfUrl);
          setComments(data.comments);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error fetching PDF URL:', err);
          setIsLoading(false);
        });
    }
  }, [chapter]);

  if (!chapter) {
    return <div>No chapter selected</div>;
  }

  function onDocumentLoadSuccess({numPages}){
    setNumPages(numPages);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert('Please enter a comment');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/chapters/${chapter.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: newComment })
      });

      if (response.ok) {
        const comment = await response.json();
        setComments([...comments, comment]);
        setNewComment('');
      } else {
        alert('Failed to add comment');
      }
    } catch (error) {
      alert('Failed to add comment');
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim()) {
      alert('Comment cannot be empty');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/chapters/${chapter.id}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: editText })
      });

      if (response.ok) {
        const updated = await response.json();
        setComments(comments.map(c => c._id === commentId ? updated : c));
        setEditingCommentId(null);
        setEditText('');
      } else {
        alert('Failed to edit comment');
      }
    } catch (error) {
      alert('Failed to edit comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      const response = await fetch(`${API_URL}/api/chapters/${chapter.id}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setComments(comments.filter(c => c._id !== commentId));
      } else {
        alert('Failed to delete comment');
      }
    } catch (error) {
      alert('Failed to delete comment');
    }
  };

  const startEdit = (comment) => {
    setEditingCommentId(comment._id);
    setEditText(comment.text);
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditText('');
  };

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-indigo-600 hover:text-indigo-800 font-medium">
        ← Back to Chapters
      </button>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{chapter.title}</h1>
        <p className="text-gray-600 mb-4">{chapter.description}</p>
        <div className="text-sm text-gray-500 mb-6">
          Uploaded on {chapter.date} • {chapter.pages} pages
        </div>


        <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-100" style={{ height: '600px' }}>
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Loading PDF from AWS S3...</div>
            </div>
          ) : pdfUrl ? (
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-auto">
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(error) => console.error('PDF load error:', error)}
                  loading={<div className="flex items-center justify-center h-96">Loading PDF...</div>}
                  error={<div className="flex items-center justify-center h-96 text-red-500">Failed to load PDF. Please try again.</div>}
                >
                  <Page 
                    pageNumber={pageNumber} 
                    width={typeof window !== 'undefined' ? Math.min(window.innerWidth - 100, 800) : 800}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>
              </div>
              
              {/* Navigation controls */}
              {numPages && (
                <div className="flex items-center justify-between bg-gray-800 text-white p-4">
                  <button
                    onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
                    disabled={pageNumber <= 1}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 rounded-md disabled:bg-gray-600 disabled:cursor-not-allowed hover:bg-indigo-700"
                  >
                    <ChevronLeft className="h-5 w-5" />
                    Previous
                  </button>
                  
                  <span className="font-medium">
                    Page {pageNumber} of {numPages}
                  </span>
                  
                  <button
                    onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))}
                    disabled={pageNumber >= numPages}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 rounded-md disabled:bg-gray-600 disabled:cursor-not-allowed hover:bg-indigo-700"
                  >
                    Next
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-500">Failed to load PDF</div>
            </div>
          )}
        </div>
        


      </div>



      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <MessageSquare className="h-6 w-6 mr-2 text-indigo-600" />
          Comments ({comments.length})
        </h2>

        <div className="space-y-4 mb-6">
          {comments.map((comment) => (
            <div key={comment._id} className="border-l-4 border-indigo-500 pl-4 py-2 bg-gray-50 rounded">
              {editingCommentId === comment._id ? (
                <div className="space-y-2">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditComment(comment._id)}
                      className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1 bg-gray-300 rounded-md text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{comment.name}</span>
                      <span className="text-xs text-gray-500">@{comment.username}</span>
                      {comment.edited && <span className="text-xs text-gray-400 italic">(edited)</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{comment.date}</span>
                      {(comment.username === user?.username || user?.role === 'master') && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => startEdit(comment)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit comment"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteComment(comment._id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Delete comment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-700">{comment.text}</p>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-3 border-t pt-6">
          <h3 className="font-semibold text-gray-900">Leave a Comment</h3>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts about this chapter..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <button
            onClick={handleAddComment}
            className="flex items-center justify-center w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors font-medium"
          >
            <Send className="h-4 w-4 mr-2" />
            Post Comment
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;

